import { Flex } from 'pink-lava-ui';
import { DataList } from '@/components/ui/list/DataList';
import type { BookingDraft, BookingDraftCount } from '@dc-trans/data-layer';
import { BookingDraftStatus } from '@dc-trans/data-layer';
import { Col, Row, Tag } from 'antd';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import dayjs from 'dayjs';
import { useBookingOnDemand, useBookingOnDemandCount } from '@/features/on-demand/api/queries';
import { Button } from 'pink-lava-ui';
import type { ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { omit } from 'es-toolkit';
import { useDownloadData } from '@/mutations';
import { useMemo, useState, type ReactNode } from 'react';
import { CircleXIcon, Clock10Icon, DownloadIcon, FileWarningIcon } from 'lucide-react';
import { triggerDownload } from '@/utils/download';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useNotification } from '@/context/notificationContext';
import OnDemandChangeLogsModal from './components/OnDemandChangeLogsModal';

const OnDemandHistoryPage = () => {
  const showNotification = useNotification();
  const { isMobile } = useBreakpoint();
  const { filters, params } = useUrlParamsContext();
  const [sortConfig, setSortConfig] = useState<{ sortBy: string; sortOrder?: SortOrder }>({
    sortBy: '',
    sortOrder: undefined,
  });
  const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);

  const handleSort = (column: string) => {
    setSortConfig(prevSort => ({
      sortBy: column,
      sortOrder: prevSort.sortBy === column && prevSort.sortOrder === 'ascend' ? 'descend' : 'ascend',
    }));
  };

  // Define default status filters
  const defaultStatusFilters = useMemo(
    () => Object.keys(omit(BookingDraftStatus, ['new', 'waiting_shipment', 'completed'])),
    []
  );

  // Apply default status filters when status filter is empty
  const apiFilters = useMemo(() => {
    const { status, ...restFilters } = filters;

    // If status is not provided or is an empty array, use default status filters
    if (!status || (Array.isArray(status) && status.length === 0)) {
      return { ...restFilters, status: defaultStatusFilters };
    }

    return filters;
  }, [filters, defaultStatusFilters]);

  // Create a custom query that uses our modified filters
  const useBookingOnDemandWithDefaults = () => {
    return useBookingOnDemand(
      {
        page: params.page,
        limit: params.limit,
        filters: {
          ...apiFilters,
          sort_by: sortConfig.sortBy,
          sort_order: sortConfig.sortOrder === 'ascend' ? 'asc' : 'desc',
        },
      },
      ['booking-on-demand-history', sortConfig.sortBy, sortConfig.sortOrder || '']
    );
  };

  const { data: bookingDraftCount } = useBookingOnDemandCount();
  const downloadDataMutation = useDownloadData({
    meta: {
      message: 'Gagal download data, silahkan coba lagi.',
    },
  });

  const mapOnDemandCount: {
    // eslint-disable-next-line no-unused-vars
    [K in keyof BookingDraftCount]: { label: string; value: number | undefined; icon: ReactNode };
  } = useMemo(() => {
    return {
      confirmation_failed: {
        label: 'Konfirmasi Belum Berhasil',
        value: bookingDraftCount?.data?.confirmation_failed,
        icon: <FileWarningIcon size={20} />,
      },
      expired: {
        label: 'Kedaluwarsa',
        value: bookingDraftCount?.data?.expired,
        icon: <Clock10Icon size={20} />,
      },
      canceled: {
        label: 'Dibatalkan',
        value: bookingDraftCount?.data?.canceled,
        icon: <CircleXIcon size={20} />,
      },
    };
  }, [bookingDraftCount]);

  const handleExport = () => {
    // Include pagination params for export as well
    const exportParams = {
      ...apiFilters,
      req_plant_delivery_date_start: apiFilters?.req_plant_delivery_date
        ? apiFilters?.req_plant_delivery_date[0]
        : undefined,
      req_plant_delivery_date_end: apiFilters?.req_plant_delivery_date
        ? apiFilters?.req_plant_delivery_date[1]
        : undefined,
      page: params.page,
      limit: params.limit,
    };

    downloadDataMutation.mutate(
      {
        url: '/booking-drafts/export',
        params: exportParams,
      },
      {
        onSuccess: ({ data, filename }) => {
          triggerDownload(data, filename);
          showNotification.success({
            message: 'Data berhasil didownload.',
          });
        },
      }
    );
  };

  const maskBookingNumber = (bookingNumber?: string) => {
    if (!bookingNumber) return '-';

    if (/^\d{9}$/.test(bookingNumber)) {
      return `${bookingNumber.slice(0, 3)}XXXXXX`;
    }
    if (/^MOCK-\d{9}$/.test(bookingNumber)) {
      const [, numericPart] = bookingNumber.split('MOCK-');
      return `MOCK-${numericPart.slice(0, 3)}XXXXXX`;
    }
    return bookingNumber;
  };

  const shouldMaskOnDemandBookingNumber = (status?: string) => {
    return status === 'new' || status === 'expired' || status === 'confirmation_failed';
  };
  
  return (
    <DataList
      title="Riwayat On Demand"
      filterFields={[
        {
          type: 'search',
          name: 'search',
          placeholder: 'Cari Nomor Booking',
          trigger: 'onChange',
        },
        {
          type: 'select',
          name: 'status',
          placeholder: 'Pilih Status',
          label: 'Status Booking',
          rounded: 'large',
          multiple: true,
          options: Object.keys(omit(BookingDraftStatus, ['new', 'waiting_shipment', 'completed'])).map(item => ({
            label: BookingDraftStatus[item as keyof typeof BookingDraftStatus],
            value: item,
          })),
        },
        {
          type: 'date-range',
          name: 'req_plant_delivery_date',
          label: 'Req. Plant Delivery Date',
          placeholder: 'Tangal Awal, Tanggal Akhir',
          rounded: 'large',
        },
      ]}
      defaultSort={{
        sortBy: 'updated_at',
        sortOrder: 'desc',
      }}
    >
      <Flex gap={16} className="w-full h-full" vertical>
        <DataList.Header
          colProps={{ md: 12, xl: 8 }}
          actions={{
            primary: {
              label: 'Export',
              onClick: handleExport,
              icon: <DownloadIcon size={16} />,
            },
          }}
        >
          <Row gutter={[8, 8]} className="mt-2">
            {Object.entries(mapOnDemandCount).map(([key, data]) => (
              <Col xs={12} lg={8} key={key}>
                <div className="border-gray-200 border-[1px] shadow-sm rounded-2xl px-3 py-1.5 items-center flex flex-row gap-2">
                  {data.icon}
                  <div className="flex text-sm font-medium flex-1 h-[40px] items-center">{data.label}</div>
                  <div className="text-xl font-bold">{typeof data.value === 'number' ? data.value : '-'}</div>
                </div>
              </Col>
            ))}
          </Row>
        </DataList.Header>
        <DataList.List<BookingDraft>
          tableSize={isMobile ? 'small' : undefined}
          useQuery={useBookingOnDemandWithDefaults}
          queryKey={['booking-on-demand-history']}
          emptyDesc="Coba gunakan filter lainnya."
          columns={[
            {
              title: 'Nomor Booking',
              dataIndex: 'tms_booking',
              key: 'tms_booking',
              fixed: !isMobile ? 'left' : undefined,
              sorter: true,
              sortOrder: sortConfig.sortBy === 'tms_booking' ? sortConfig.sortOrder : undefined,
              onHeaderCell: () => ({
                onClick: () => handleSort('tms_booking'),
                style: { cursor: 'pointer' },
              }),
              render: (value: string, record: BookingDraft) =>
                shouldMaskOnDemandBookingNumber(record.status) ? maskBookingNumber(value) : value,
            } as ColumnType<BookingDraft>,
            {
              title: 'Dibuat Pada',
              dataIndex: 'created_at',
              key: 'created_at',
              render: (text: string) => (text ? dayjs(text).format('DD MMMM YYYY HH:mm:ss') : '-'),
            } as ColumnType<BookingDraft>,
            {
              title: 'Req. Plant Delivery Date',
              dataIndex: 'req_plant_delivery_date',
              key: 'req_plant_delivery_date',
              render: (text: string) => (text ? dayjs(text).format('DD MMMM YYYY') : '-'),
            } as ColumnType<BookingDraft>,
            {
              title: 'Req. Shipping Window',
              dataIndex: 'req_ship_window',
              key: 'req_ship_window',
              render: (record: any) => (record ? `${record.incoming} - ${record.outgoing}` : '-'),
            } as ColumnType<BookingDraft>,
            {
              title: 'Req. Jenis Kendaraan',
              dataIndex: 'req_vehicle_type',
              key: 'req_vehicle_type',
              render: (record: any) => record?.name || '-',
            } as ColumnType<BookingDraft>,
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              align: 'center',
              width: 180,
              render: (_: any, record: BookingDraft) => {
                let color = 'default';
                switch (record.status) {
                  case 'new':
                    color = 'blue';
                    break;
                  case 'waiting_shipment':
                    color = 'orange';
                    break;
                  case 'completed':
                    color = 'green';
                    break;
                  case 'confirmation_failed':
                  case 'canceled':
                  case 'expired':
                    color = 'error';
                    break;
                  default:
                    color = 'default';
                    break;
                }

                return (
                  <Tag className={`rounded-full! mr-0!`} bordered={false} color={color}>
                    {BookingDraftStatus[record.status]}
                  </Tag>
                );
              },
            } as ColumnType<BookingDraft>,
            {
              title: 'Aksi',
              fixed: !isMobile ? 'right' : undefined,
              align: 'center',
              width: 120,
              render: (_: any, record: BookingDraft) => {
                return (
                  <Flex gap={8} justify="center">
                    <Button
                      type="tertiary"
                      size="small"
                      shape="round"
                      onClick={() => setSelectedBookingId(record.ulid)}
                    >
                      Lihat Log
                    </Button>
                  </Flex>
                );
              },
            } as ColumnType<BookingDraft>,
          ]}
        />

        <OnDemandChangeLogsModal
          isOpen={!!selectedBookingId}
          onClose={() => setSelectedBookingId(undefined)}
          bookingId={selectedBookingId}
        />
      </Flex>
    </DataList>
  );
};

export default OnDemandHistoryPage;
